import { HttpErrorResponse } from "@angular/common/http";

export interface ChangeSetPreviewRequest {
  configurationCode: string;
}

export interface ChangeSetEntry {
  domainCode: string;
  changeKind: string;
  subjectCode: string;
  beforeValue?: string | null;
  afterValue?: string | null;
  impactedArea: string;
  configurationCode: string;
  versionCode: string;
  versionStatusCode: string;
  entryId?: number | null;
  canPublish: boolean;
  canRollback: boolean;
}

export interface ChangeSetPreview {
  configurationCode: string;
  pendingChangeCount: number;
  entries: ChangeSetEntry[];
}

export interface ChangeSetImpactSummaryRow {
  domainCode: string;
  changeCount: number;
}

export interface ChangeSetWorkspaceResult<T> {
  state: ChangeSetWorkspaceState;
  data: T | null;
  error: ChangeSetWorkspaceDisplayError | null;
}

export type ChangeSetWorkspaceState = "resolved" | "invalid" | "missing" | "conflict" | "unavailable";

export interface ChangeSetWorkspaceDisplayError {
  title: string;
  messages: string[];
  statusCode?: number;
  reasonCode?: string;
  correlationId?: string | null;
}

export interface ChangeSetWorkspaceProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  reasonCode?: string;
  errors?: Record<string, string[] | string>;
}

export function toChangeSetSuccess<T>(data: T): ChangeSetWorkspaceResult<T> {
  return {
    state: "resolved",
    data,
    error: null,
  };
}

export function toChangeSetFailure<T>(error: unknown): ChangeSetWorkspaceResult<T> {
  const displayError = mapChangeSetWorkspaceError(error);

  return {
    state: toWorkspaceState(displayError.statusCode),
    data: null,
    error: displayError,
  };
}

export function mapChangeSetWorkspaceError(error: unknown): ChangeSetWorkspaceDisplayError {
  if (error instanceof HttpErrorResponse) {
    const problem = toProblemDetails(error.error);
    const statusCode = error.status || problem.status;
    const messages = new Set<string>();

    flattenValidationMessages(problem.errors).forEach((message) => messages.add(message));
    if (statusCode !== undefined && statusCode < 500 && normalizeText(problem.detail).length > 0) {
      messages.add(normalizeText(problem.detail));
    }

    if (messages.size === 0) {
      messages.add(getDefaultErrorMessage(statusCode));
    }

    return {
      title: statusCode !== undefined && statusCode >= 500
        ? getDefaultErrorTitle(statusCode)
        : normalizeText(problem.title) || getDefaultErrorTitle(statusCode),
      messages: [...messages],
      statusCode,
      reasonCode: normalizeText(problem.reasonCode) || undefined,
      correlationId: error.headers?.get("X-Correlation-ID"),
    };
  }

  return {
    title: "Unexpected error",
    messages: ["Change-set workspace could not be loaded. Try again or contact support if the problem persists."],
  };
}

export function toImpactSummary(entries: ChangeSetEntry[]): ChangeSetImpactSummaryRow[] {
  const counts = new Map<string, number>();
  entries.forEach((entry) => {
    counts.set(entry.domainCode, (counts.get(entry.domainCode) ?? 0) + 1);
  });

  return [...counts.entries()]
    .map(([domainCode, changeCount]) => ({ domainCode, changeCount }))
    .sort((left, right) => left.domainCode.localeCompare(right.domainCode));
}

function toWorkspaceState(statusCode?: number): ChangeSetWorkspaceState {
  switch (statusCode) {
    case 400:
      return "invalid";
    case 404:
      return "missing";
    case 409:
      return "conflict";
    default:
      return "unavailable";
  }
}

function toProblemDetails(errorBody: unknown): ChangeSetWorkspaceProblemDetails {
  if (!errorBody || typeof errorBody !== "object" || Array.isArray(errorBody)) {
    return {};
  }

  return errorBody as ChangeSetWorkspaceProblemDetails;
}

function flattenValidationMessages(errors: ChangeSetWorkspaceProblemDetails["errors"]): string[] {
  if (!errors || typeof errors !== "object") {
    return [];
  }

  return Object.values(errors).flatMap((entry) => {
    if (Array.isArray(entry)) {
      return entry
        .map((item) => normalizeText(item))
        .filter((item) => item.length > 0);
    }

    const normalized = normalizeText(entry);
    return normalized.length > 0 ? [normalized] : [];
  });
}

function getDefaultErrorTitle(statusCode?: number): string {
  switch (statusCode) {
    case 400:
      return "Request could not be completed";
    case 404:
      return "Change set not found";
    case 409:
      return "Change set conflict detected";
    default:
      return "Change-set workspace unavailable";
  }
}

function getDefaultErrorMessage(statusCode?: number): string {
  switch (statusCode) {
    case 400:
      return "Review the configuration code, then try again.";
    case 404:
      return "No pending change set was found for the supplied configuration.";
    case 409:
      return "The change set conflicts with the current published state.";
    default:
      return "Change-set workspace could not be loaded. Try again or contact support if the problem persists.";
  }
}

function normalizeText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
